// Constants
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
// Models
import { ContextDisplayType } from "@ms/uno-models/lib/local/client/legacyPlanner/plan/ContextDisplayType";
import { getSyncContextDisplayText } from "@ms/uno-models/lib/local/client/legacyPlanner/plan/PlanContextUtilities";
// MS branded logo icons
import loopSvg from "@ms/uno-resources/lib/local/images/logos/loop.svg";
// Styles
import { getClassNames, getStyles } from "./ExternalContextLink.styles";
// Utilities
import * as React from "react";
import * as StringHelper from "@fluentui/utilities";
import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";
import findIndex from "lodash/findIndex";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import * as BrowserUtilities from "@ms/uno-utilities/lib/local/BrowserUtilities";
import { useTheme } from "@fluentui/react";
import { useLoggers } from "../../../../hooks/useLoggers";
/**
 * External Context Link component
 */ export function ExternalContextLink(props) {
    const { styles, plan, planDetails, planContainer, enableExternalUrlRegexCheck } = props;
    const { engagementLogger, traceLogger } = useLoggers();
    const theme = useTheme();
    const classNames = getClassNames(getStyles(theme, styles));
    const meetingNotesContextKey = getExternalContextKey(ContextDisplayType.MeetingNotes, plan, planDetails);
    const loopPageContextKey = getExternalContextKey(ContextDisplayType.LoopPage, plan, planDetails);
    let planContext;
    let planContextDetails;
    if (!meetingNotesContextKey && !loopPageContextKey) {
        return null;
    }
    if (meetingNotesContextKey) {
        planContext = plan.contexts[meetingNotesContextKey];
        planContextDetails = planDetails.contextDetails[meetingNotesContextKey];
    } else if (loopPageContextKey) {
        planContext = plan.contexts[loopPageContextKey];
        planContextDetails = planDetails.contextDetails[loopPageContextKey];
    } else {
        return null;
    }
    const externalContextLink = getExternalContextLinkProperties(planContext, planContextDetails.displayAs, planContainer, planDetails, plan);
    if (!externalContextLink) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("button", {
        className: classNames.root,
        "aria-label": externalContextLink.ariaLabel,
        onClick: (ev)=>openExternalContextLink(ev, externalContextLink.webUrl, engagementLogger, traceLogger, enableExternalUrlRegexCheck),
        onKeyUp: (ev)=>openExternalContextLink(ev, externalContextLink.webUrl, engagementLogger, traceLogger, enableExternalUrlRegexCheck)
    }, /*#__PURE__*/ React.createElement("img", {
        src: externalContextLink.svg,
        className: classNames.externalContextIcon,
        alt: Strings.TaskEditor_ExternalContextIconTitle,
        title: Strings.TaskEditor_ExternalContextIconTitle
    }), /*#__PURE__*/ React.createElement("span", {
        className: classNames.externalContextLinkLabel
    }, externalContextLink.linkLabel), ";");
}
const getExternalContextKey = (contextDisplayType, plan, planDetails)=>{
    if (isEmpty(plan.contexts)) {
        return null;
    }
    const contextKeys = Object.keys(plan.contexts);
    // Sort the context key by corresponding context's createdDate in ascending order and by app id
    const sortedContextKeys = sortBy(contextKeys, [
        (key)=>plan.contexts[key].createdDate
    ], [
        (key)=>plan.contexts[key].ownerAppId
    ]);
    const contextIndex = findIndex(sortedContextKeys, (key)=>{
        const planDetailsEntity = planDetails.contextDetails[key];
        if (planDetailsEntity == null) {
            return false;
        }
        return planDetailsEntity.displayAs === contextDisplayType;
    });
    if (contextIndex === -1) {
        return null;
    }
    return sortedContextKeys[contextIndex];
};
/**
 * Get external context link url
 * Note: Currently this is scope down to Meeting Notes and Loop Page only
 * @param plan Plan object for the task
 * @param contextDisplayType Context display type
 */ const getExternalContextLinkUrl = (contextDisplayType, planDetails, plan)=>{
    const contextKey = getExternalContextKey(contextDisplayType, plan, planDetails);
    if (!contextKey) {
        return null;
    }
    return planDetails.contextDetails[contextKey].url ?? null;
};
/**
 * Helper method to get the external context link properties
 * @param planContext Plan context
 * @param contextDisplayType Context display type
 */ const getExternalContextLinkProperties = (planContext, contextDisplayType, planContainer, planDetails, plan)=>{
    let linkLabel;
    let ariaLabel;
    let svg;
    switch(contextDisplayType){
        case ContextDisplayType.MeetingNotes:
            svg = loopSvg;
            linkLabel = Strings.PlanContextLinking_MeetingNotes;
            ariaLabel = StringHelper.format(Strings.PlanContextLinking_ExternalContextLinkAriaLabel, Strings.PlanContextLinking_MeetingNotes, Strings.PlanContextLinking_MicrosoftLoop);
            break;
        case ContextDisplayType.LoopPage:
            svg = loopSvg;
            linkLabel = Strings.PlanContextLinking_MicrosoftLoop;
            ariaLabel = StringHelper.format(Strings.PlanContextLinking_ExternalContextLinkAriaLabel, getSyncContextDisplayText(planContext, planContainer?.displayName ?? ""), Strings.PlanContextLinking_MicrosoftLoop);
            break;
        default:
            return null;
    }
    return {
        linkLabel: linkLabel,
        ariaLabel: ariaLabel,
        svg: svg,
        webUrl: getExternalContextLinkUrl(contextDisplayType, planDetails, plan)
    };
};
/**
 * Open external context link file in new window
 * @param e The mouse/keyboard event that was fired
 * @param url Web url of the external context link
 * @param engagementLogger Engagement logger
 * @param traceLogger Trace logger
 * @param enableExternalUrlRegexCheck Enable external url regex check
 */ const openExternalContextLink = (e, url, engagementLogger, traceLogger, enableExternalUrlRegexCheck)=>{
    if (url && KeyboardUtilities.isButtonPress(e)) {
        engagementLogger.logEngagement(EngagementTelemetryConstants.ExternalContextLink, EngagementTelemetryConstants.TaskDetailsExternalContextLinkClicked);
        e.preventDefault();
        BrowserUtilities.safelyOpenExternalUrl(url, traceLogger, enableExternalUrlRegexCheck, "_blank");
    }
};
